import { useEffect, useRef } from "react";

// Лёгкий Lottie-плеер для фирменных анимаций Telegram (уточка, обезьянка 2FA).
// Плеер и JSON подгружаются лениво, чтобы не тянуть lottie-web в основной бандл.
// Чисто презентационный компонент: анимация зациклена, без интерактива.

type LottieAnimation = {
  destroy(): void;
};

type LottiePlayer = {
  loadAnimation(params: {
    container: HTMLElement;
    renderer: "svg";
    loop: boolean;
    autoplay: boolean;
    animationData: unknown;
  }): LottieAnimation;
};

let playerPromise: Promise<LottiePlayer> | null = null;

const loadPlayer = (): Promise<LottiePlayer> => {
  playerPromise ??= import("lottie-web/build/player/lottie_light.js").then(
    (module) => (module.default ?? module) as unknown as LottiePlayer
  );
  return playerPromise;
};

const animationDataCache = new Map<string, Promise<unknown>>();

const loadAnimationData = (src: string): Promise<unknown> => {
  let cached = animationDataCache.get(src);
  if (!cached) {
    cached = fetch(src).then((response) => response.json());
    animationDataCache.set(src, cached);
  }
  return cached;
};

interface LottieStickerProps {
  /** Путь к lottie-JSON (например, "/tg/tgs/EmptyFolder.json"). */
  src: string;
  size?: number;
  loop?: boolean;
  className?: string;
}

export const LottieSticker = ({ src, size = 120, loop = true, className }: LottieStickerProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let animation: LottieAnimation | null = null;
    let cancelled = false;

    void Promise.all([loadPlayer(), loadAnimationData(src)]).then(([player, animationData]) => {
      if (cancelled || !containerRef.current) {
        return;
      }
      containerRef.current.innerHTML = "";
      animation = player.loadAnimation({
        container: containerRef.current,
        renderer: "svg",
        loop,
        autoplay: true,
        animationData
      });
    });

    return () => {
      cancelled = true;
      animation?.destroy();
    };
  }, [loop, src]);

  return (
    <div
      ref={containerRef}
      className={className ? `lottie-sticker ${className}` : "lottie-sticker"}
      style={{ width: size, height: size }}
      aria-hidden="true"
    />
  );
};
